import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useStoreContext } from '../context/StoreContext';

// WhatsApp SVG icon — official brand color, no external dependencies
function WhatsAppIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

export default function WhatsAppWidget() {
  const { settings, store } = useStoreContext();
  const location = useLocation();

  // Build the WhatsApp URL with smart product context
  const waUrl = useMemo(() => {
    const phone = settings?.whatsapp_phone;
    if (!phone) return null;

    const baseMessage = settings?.whatsapp_message
      || `Hi! I have a question about ${store?.store_name || 'your store'}.`;

    // Detect if we're on a product page and enrich the message
    const productMatch = location.pathname.match(/^\/products\/(.+)/);
    let finalMessage = baseMessage;

    if (productMatch) {
      // Get product title from the page's document title if available
      // This works because ProductDetail.tsx sets document.title
      const pageTitle = document.title
        .replace(` | ${store?.store_name}`, '')
        .replace(` - ${store?.store_name}`, '')
        .trim();

      const productUrl = `${window.location.origin}${location.pathname}`;

      if (pageTitle && pageTitle !== store?.store_name) {
        finalMessage = `${baseMessage}\n\nProduct: ${pageTitle}\n${productUrl}`;
      } else {
        finalMessage = `${baseMessage}\n\n${productUrl}`;
      }
    }

    return `https://wa.me/${phone}?text=${encodeURIComponent(finalMessage)}`;
  }, [settings?.whatsapp_phone, settings?.whatsapp_message, location.pathname, store?.store_name]);

  // Don't render if WhatsApp is not configured or disabled
  if (!settings?.whatsapp_enabled || !waUrl) return null;

  return (
    <>
      {/* All styles are inline — zero theme bleed, works on default and Nova themes */}
      <a
        className="wa-widget-link"
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        title="Chat on WhatsApp"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9998, // below Aria orb (9999) on dashboard, but storefront has no Aria
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#25D366',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(37,211,102,0.4), 0 2px 8px rgba(0,0,0,0.15)',
          cursor: 'pointer',
          textDecoration: 'none',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={e => {
          const el = e.currentTarget as HTMLAnchorElement;
          el.style.transform = 'scale(1.1)';
          el.style.boxShadow = '0 6px 24px rgba(37,211,102,0.55), 0 3px 12px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={e => {
          const el = e.currentTarget as HTMLAnchorElement;
          el.style.transform = 'scale(1)';
          el.style.boxShadow = '0 4px 16px rgba(37,211,102,0.4), 0 2px 8px rgba(0,0,0,0.15)';
        }}
      >
        <WhatsAppIcon size={30} />

        {/* Pulse ring animation */}
        <span style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '2px solid rgba(37,211,102,0.6)',
          animation: 'waPulse 2.5s ease-out infinite',
          pointerEvents: 'none',
        }} />
      </a>

      {/* Tooltip on hover — shows only on desktop */}
      <style>{`
        @keyframes waPulse {
          0%   { transform: scale(1);   opacity: 0.8; }
          70%  { transform: scale(1.5); opacity: 0;   }
          100% { transform: scale(1.5); opacity: 0;   }
        }
        @media (max-width: 767px) {
          .wa-widget-link {
            bottom: calc(24px + env(safe-area-inset-bottom, 0px)) !important;
            right: 20px !important;
          }
        }
        @media (min-width: 768px) {
          .wa-widget-link::before {
            content: 'Chat with us';
            position: absolute;
            right: 64px;
            bottom: 50%;
            transform: translateY(50%);
            background: #1a1a2e;
            color: white;
            padding: 6px 12px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            white-space: nowrap;
            opacity: 0;
            transition: opacity 0.2s;
            pointer-events: none;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
          .wa-widget-link:hover::before {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
